
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { submitOrder } from '../api/orders';
import { getImageUrl } from '../utils/image';
import { formatPrice } from '../utils/formatPrice';

import { validateMoroccanPhone } from '../utils/phoneValidator';
import { Link, useNavigate } from 'react-router-dom';

const Checkout: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { items, total, clearCart } = useCart();
    const navigate = useNavigate();
    const isRTL = i18n.dir() === 'rtl';

    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        city: '',
        address: '',
        note: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validateMoroccanPhone(formData.phone)) {
            setError(t('validation.invalidPhone', 'رقم الهاتف غير صحيح'));
            return;
        }

        setLoading(true);
        try {
            await submitOrder({
                items: items.map(item => ({
                    product_id: item.product.id,
                    quantity: item.quantity,
                    product_variant_id: item.variant?.id
                })),
                ...formData
            });
            clearCart();
            navigate('/thank_you');
        } catch (err) {
            setError('حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى.');
        } finally {
            setLoading(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="container mx-auto px-4 py-16 text-center">
                <h2 className="text-2xl font-bold mb-4">{t('common.cart')} فارغة</h2>
                <Link to="/" className="text-primary hover:underline">
                    {t('home.shopNow')}
                </Link>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold text-neutral-900 mb-8">{t('common.checkout')}</h1>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Order Summary */}
                <div className="bg-neutral-50 p-6 rounded-2xl h-fit">
                    <h3 className="text-xl font-bold mb-4">{t('common.total')}</h3>
                    <ul className="space-y-4 mb-6">
                        {items.map((item) => (
                            <li key={`${item.product.id}-${item.variant?.id || 'default'}`} className="flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <img src={getImageUrl(item.product.images[0])} alt="" className="w-12 h-12 rounded-md object-cover" />
                                    <div>
                                        <p className="font-medium text-sm">
                                            {isRTL ? item.product.title_ar : item.product.title_fr}
                                            {item.variant && <span className="text-xs text-gray-500 block">({item.variant.name})</span>}
                                        </p>
                                        <p className="text-xs text-neutral-500">x{item.quantity}</p>
                                    </div>
                                </div>
                                <span className="font-medium">
                                    {formatPrice(
                                        (item.variant ? parseFloat(item.variant.price) : (item.product.discount_price || item.product.price)) * item.quantity,
                                        item.product.currency,
                                        isRTL ? 'ar-MA' : 'fr-MA'
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <div className="border-t border-neutral-200 pt-4 flex justify-between items-center text-xl font-bold">
                        <span>{t('common.total')}</span>
                        <span className="text-primary">
                            {formatPrice(total, 'MAD', isRTL ? 'ar-MA' : 'fr-MA')}
                        </span>
                    </div>
                </div>

                {/* Form */}
                <div>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                الاسم الكامل
                            </label>
                            <input
                                type="text"
                                name="name"
                                id="name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                            />
                        </div>

                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                رقم الهاتف
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                id="phone"
                                required
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                                dir="ltr"
                            />
                        </div>

                        <div>
                            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                                المدينة
                            </label>
                            <input
                                type="text"
                                name="city"
                                id="city"
                                required
                                value={formData.city}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                            />
                        </div>

                        <div>
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                                عنوان التوصيل
                            </label>
                            <input
                                type="text"
                                name="address"
                                id="address"
                                required
                                value={formData.address}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                            />
                        </div>

                        <div>
                            <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
                                ملاحظات
                            </label>
                            <textarea
                                name="note"
                                id="note"
                                rows={2}
                                value={formData.note}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                            />
                        </div>

                        {error && (
                            <p className="text-red-500 text-sm">{error}</p>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full inline-flex justify-center rounded-full border border-transparent bg-primary px-6 py-4 text-lg font-bold text-white hover:bg-green-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? t('common.loading') : t('common.checkout')}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Checkout;
